import { Box, Checkbox } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';

import {
    CellValueChangedEvent,
    ColumnMovedEvent,
    ColumnResizedEvent,
    RowSelectedEvent
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './productstyles.css';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import '../../common/datagrid/table-style.css';
import { RootState } from '../../store';
import { ProductRow } from './productTypes';
import {
    getColumnDefs,
    getIsProductsLoading,
    getProductRows,
    getProductsTable
} from './redux/productSelectors';
import { productSelected, productTableActions, updateProduct } from './redux/productSlices';

const mapStateToProps = (state: RootState) => ({
    isLoading: getIsProductsLoading(state),
    products: getProductRows(state),
    columns: getColumnDefs(state),
    productsTable: getProductsTable(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const ProductTable = ({ products, columns, productsTable }: Props) => {
    const dispatch = useDispatch();

    const gridRef = useRef<AgGridReact>(null);
    const cols = useMemo(
        () =>
            columns.map(c => {
                if (c.field !== 'hidePrices') {
                    return c;
                }

                return {
                    ...c,
                    sortable: true,
                    cellRenderer: props => {
                        const onChange = () =>
                            dispatch(
                                updateProduct.request({
                                    productId: props.data.productId,
                                    dto: {
                                        ...props.data,
                                        hidePrices: !props.value,
                                        name: props.data.productName
                                    }
                                })
                            );
                        return (
                            <Checkbox
                                style={{ paddingTop: '0px' }}
                                checked={props.data.hidePrices as boolean}
                                onChange={onChange}
                            />
                        );
                    }
                };
            }),
        [columns, dispatch]
    );

    const { resizableColumns, saveColumnWidth } = useAgTable({
        columns: cols,
        rows: products,
        tableActions: productTableActions,
        tableSettings: productsTable,
        gridRef: gridRef
    });
    const onRowsChange = useCallback(
        ({ data, rowIndex }: CellValueChangedEvent<ProductRow>) => {
            if (rowIndex != null) {
                dispatch(
                    updateProduct.request({
                        productId: data.productId,
                        dto: {
                            ...data,
                            name: data.productName
                        }
                    })
                );
            }
        },
        [dispatch]
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getRowId = (params: any) => params.data.productId;

    const [draggedColumn, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);
    const handleRowSelected = (event: RowSelectedEvent<ProductRow>) => {
        if (event.node.data?.productId != null) {
            dispatch(productSelected(event.node.data?.productId));
        }
    };
    const rows = useMemo(() => {
        return products;
    }, [products]);
    return (
        <Box style={{ height: '82vh' }}>
            <AgGridReact
                className="ag-theme-alpine"
                columnDefs={resizableColumns}
                getRowId={getRowId}
                rowData={rows}
                onCellValueChanged={onRowsChange}
                onFirstDataRendered={setupGrid}
                floatingFiltersHeight={30}
                rowSelection="single"
                containerStyle={{
                    height: '82vh',
                    // Always show y-scroll because it fixes flickering issues
                    summaryBorderColor: '#aaa',
                    backgroundColor: 'hsl(0deg 0% 100%)',
                    headerBackgroundColor: 'hsl(0deg 0% 97.5%)'
                }}
                onDragStopped={() => {
                    if (draggedColumn != null && draggedColumn.colId != '') {
                        dispatch(
                            productTableActions.columnsReordered({
                                sourceColumn: draggedColumn?.colId,
                                targetColumn: cols[draggedColumn?.toIndex].field ?? ''
                            })
                        );
                        setDraggedColumn(null);
                    }
                }}
                onRowSelected={handleRowSelected}
                onColumnResized={(event: ColumnResizedEvent<ProductRow>) => {
                    if (
                        event.finished &&
                        event.source == 'uiColumnDragged' &&
                        event.type == 'columnResized'
                    ) {
                        const colState = event.columnApi.getColumnState();

                        const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                        saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                    }
                }}
                onColumnMoved={(event: ColumnMovedEvent<ProductRow>) => {
                    setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
                }}
            />
        </Box>
    );
};

export default connect(mapStateToProps)(ProductTable);
